import { useSnackbar } from "notistack";
import { FC, useEffect, useRef, useState } from "react";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";

import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";


interface AdminReportUsers {
    data: IccReportData;
    error: IError;
}
interface IccReportData {
    users: ReportUserDisplayModel[];
}

interface DeleteReportUserRequest {
    id: number;
}

interface DeleteReportUserResponse {
    data: DeleteReportUserData;
    error: IError;
}
interface DeleteReportUserData {
    status: string;
}

function ColyToClipBoard(text: string) {
    navigator.clipboard.writeText(text);
};

const headerHeight: number = 115;

export interface ReportUserDisplayModel {
    id: number;
    username: string;
    password: string;
    companyName: string;
    imagesType: string;
    boothIdFilter: string;
}

interface ViewReportUserProsp {
    editUser: (user: ReportUserDisplayModel) => void;
}

const ViewReportUser: FC<ViewReportUserProsp> = ({ editUser }) => {
    const columns = [
        { field: 'id', headerName: 'ID', width: 10 },
        { field: 'username', headerName: 'Username', width: 200 },
        {
            field: 'password', headerName: 'Password', width: 200,
            renderCell: (param: any) => (
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent="space-between"
                    sx={{ width: "200px" }}>
                    <p>{param.value}</p>

                    <IconButton onClick={() => { ColyToClipBoard(`u: ${param.row.username}\np: ${param.value}`) }}>
                        <ContentCopyIcon />
                    </IconButton>
                </Stack>
            )
        },
        { field: 'companyName', headerName: 'Company', width: 200 },
        { field: 'imagesType', headerName: 'Image Type', width: 150 },
        { field: 'boothIdFilter', headerName: 'BoothId Filter', width: 200 },
        {
            field: 'edit', headerName: 'Edit user', width: 80,
            renderCell: (param: any) => (
                <IconButton onClick={() => { editUser(param.row) }}>
                    <EditIcon />
                </IconButton>
            )
        },
        {
            field: 'delete', headerName: 'Delete user', width: 100,
            renderCell: (param: any) => (
                <IconButton onClick={() => { AskToDeleteUser(param.row) }}>
                    <DeleteForeverIcon htmlColor="red" />
                </IconButton>
            )
        }
    ];

    const { enqueueSnackbar } = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 50,
        page: 0,
    });
    const [users, setUsers] = useState<ReportUserDisplayModel[]>([]);

    const userToDelete = useRef<ReportUserDisplayModel | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);

    useEffect(() => {
        GetIccUsers();
    }, []);

    const GetIccUsers = async () => {
        try {
            const response: AdminReportUsers = await api.get("api/Admin/GetReportUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AskToDeleteUser(user: ReportUserDisplayModel) {
        userToDelete.current = user;
        setDeleteConfirmation(true);
    }

    function closeDeleteConfirmation() {
        setDeleteConfirmation(false);
    }

    const DeleteUser = async () => {
        if (userToDelete.current == null)
            return;

        var body: DeleteReportUserRequest = {
            id: userToDelete.current.id
        }

        try {
            const response: DeleteReportUserResponse = await api.post("api/Admin/DeleteReportUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Deleted!", { variant: "success" });

                // get new users from db
                GetIccUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function GetGridHeight() {
        return window.innerHeight - headerHeight;
    }


    return (
        <>
            <DataGrid
                sx={{ height: GetGridHeight() }}
                rows={users}
                columns={columns}
                pagination
                pageSizeOptions={[10, 15, 20, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 100 },
                    },
                }} />
            <Dialog
                open={deleteConfirmation}
                onClose={closeDeleteConfirmation}>
                <DialogTitle>
                    {"Report user delete confirmation"}
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to remove this user permanently?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteConfirmation}>Cancel</Button>
                    <Button color="error" onClick={() => {
                        closeDeleteConfirmation();
                        DeleteUser();
                    }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog >
        </>
    )
}

export default ViewReportUser;